import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { VehicleCard } from "@/components/site/VehicleCard";
import { VehicleModal } from "@/components/site/VehicleModal";
import { useVehicles } from "@/application/hooks/use-vehicles";
import { uniqueSorted, type Vehicle } from "@/domain/models/vehicle";
import { repositories } from "@/infrastructure/repositories";
import { siteLocation } from "@/data/location";
import { useI18n } from "@/i18n";
import heroImg from "@/assets/hero-car.jpg";
import { toast } from "sonner";
import {
  ArrowRight,
  Award,
  ShieldCheck,
  Sparkles,
  Wallet,
  Clock,
  Handshake,
  Star,
  MapPin,
  Phone,
  Mail,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { usePageMeta } from "@/hooks/use-page-meta";

export function HomePage() {
  const { t } = useI18n();
  usePageMeta({
    title: t.meta.homeTitle,
    description: t.meta.homeDescription,
  });

  const { data: vehicles } = useVehicles();
  const inventory = vehicles ?? [];
  const brands = uniqueSorted(inventory.map((v) => v.brand));

  const [active, setActive] = useState<Vehicle | null>(null);
  const [open, setOpen] = useState(false);
  const openVehicle = (v: Vehicle) => {
    setActive(v);
    setOpen(true);
  };

  return (
    <>
      <main>
        <Hero />
        <Marquee brands={brands} />
        <Featured vehicles={inventory} onOpen={openVehicle} />
        <Brands />
        <Promotions />
        <Financing />
        <Testimonials />
        <About />
        <FAQ />
        <Contact vehicles={inventory} />
      </main>
      <VehicleModal vehicle={active} open={open} onOpenChange={setOpen} />
    </>
  );
}

/* ---------------- Hero ---------------- */

function Hero() {
  const { t } = useI18n();
  const root = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!root.current) return;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
      tl.from(".hero-eyebrow", { y: 24, opacity: 0, duration: 0.7, clearProps: "all" })
        .from(".hero-title span", { y: 60, opacity: 0, duration: 0.9, stagger: 0.08, clearProps: "all" }, "-=0.4")
        .from(".hero-copy", { y: 24, opacity: 0, duration: 0.7, clearProps: "all" }, "-=0.5")
        .from(".hero-cta", { y: 20, opacity: 0, duration: 0.6, stagger: 0.1, clearProps: "all" }, "-=0.4")
        .from(".hero-stat", { y: 20, opacity: 0, duration: 0.6, stagger: 0.1, clearProps: "all" }, "-=0.3")
        // Keep image visible — only scale, never fade to blank
        .from(".hero-image", { scale: 1.08, duration: 1.4, ease: "power2.out", clearProps: "transform" }, 0);
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={root} className="relative isolate min-h-[100svh] overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <img
          src={heroImg}
          alt=""
          width={1920}
          height={1200}
          className="hero-image h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/60 to-background" />
        <div className="absolute inset-0 bg-[radial-gradient(60%_60%_at_70%_30%,oklch(0.72_0.17_245/0.18),transparent_70%)]" />
      </div>

      <div className="mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-end px-4 pb-16 pt-32 sm:px-6 sm:pb-24 lg:px-8">
        <div className="max-w-3xl">
          <div className="hero-eyebrow inline-flex items-center gap-2 rounded-full border border-border bg-background/40 px-4 py-1.5 backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--color-primary)]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
              {t.hero.eyebrow}
            </span>
          </div>

          <h1 className="hero-title mt-6 font-display text-5xl font-bold leading-[1.02] tracking-tight sm:text-6xl md:text-7xl lg:text-[92px]">
            <span className="block">{t.hero.titleLine1}</span>
            <span className="block text-gradient-ice">{t.hero.titleLine2}</span>
          </h1>

          <p className="hero-copy mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            {t.hero.copy}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/catalog"
              className="hero-cta group inline-flex h-12 items-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition-all hover:-translate-y-0.5"
            >
              {t.hero.explore}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/#contact"
              className="hero-cta inline-flex h-12 items-center gap-2 rounded-full border border-border bg-background/40 px-6 text-sm font-semibold text-foreground backdrop-blur transition-colors hover:bg-background/70"
            >
              {t.hero.bookPrivate}
            </Link>
          </div>

          <div className="mt-14 grid max-w-2xl grid-cols-3 gap-6 border-t border-border/60 pt-8">
            {t.hero.stats.map((s) => (
              <div key={s.k} className="hero-stat">
                <div className="font-display text-3xl font-bold text-foreground">{s.k}</div>
                <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="pointer-events-none absolute bottom-6 left-1/2 hidden -translate-x-1/2 animate-bounce text-xs text-muted-foreground sm:block">
        <div className="mx-auto h-8 w-5 rounded-full border border-border">
          <div className="mx-auto mt-1.5 h-1.5 w-1 rounded-full bg-primary" />
        </div>
      </div>
    </section>
  );
}

/* ---------------- Marquee ---------------- */

function Marquee({ brands }: { brands: string[] }) {
  const items = [...brands, "Aston Martin", "Porsche", "Lamborghini", "Maserati"];
  return (
    <div className="relative overflow-hidden border-y border-border bg-surface py-6">
      <div className="flex animate-[marquee_40s_linear_infinite] gap-16 whitespace-nowrap">
        {[...items, ...items, ...items].map((b, i) => (
          <span
            key={i}
            className="font-display text-2xl font-semibold tracking-tight text-muted-foreground/60 sm:text-3xl"
          >
            {b}
          </span>
        ))}
      </div>
      <style>{`@keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-33.333%); } }`}</style>
    </div>
  );
}

/* ---------------- Featured ---------------- */

function Featured({
  vehicles,
  onOpen,
}: {
  vehicles: Vehicle[];
  onOpen: (v: Vehicle) => void;
}) {
  const { t, tf } = useI18n();
  const root = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!root.current) return;
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      gsap.from(".featured-heading > *", {
        y: 40,
        opacity: 0,
        duration: 0.8,
        stagger: 0.1,
        ease: "power3.out",
        clearProps: "all",
        scrollTrigger: { trigger: root.current, start: "top 80%" },
      });
    }, root);
    return () => ctx.revert();
  }, []);

  const featured = vehicles.slice(0, 6);

  return (
    <section ref={root} id="featured" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="featured-heading flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div className="max-w-xl">
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary">
              {t.featured.eyebrow}
            </div>
            <h2 className="mt-3 font-display text-4xl font-bold tracking-tight sm:text-5xl">
              {t.featured.title}
            </h2>
            <p className="mt-4 text-muted-foreground">{t.featured.copy}</p>
          </div>
          <Link
            to="/catalog"
            className="group inline-flex items-center gap-2 self-start font-mono text-xs uppercase tracking-[0.2em] text-primary hover:underline sm:self-end"
          >
            {tf(t.featured.viewAll, { count: vehicles.length })}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((v, i) => (
            <VehicleCard key={v.id} vehicle={v} onOpen={onOpen} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Brands ---------------- */

function Brands() {
  const { t } = useI18n();
  return (
    <section className="border-t border-border bg-surface py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary">
            {t.brands.eyebrow}
          </div>
          <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl">{t.brands.title}</h2>
        </div>
        <div className="mt-12 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-3 lg:grid-cols-6">
          {["Audi", "BMW", "Mercedes-AMG", "Aston Martin", "Polestar", "Ford"].map((b) => (
            <div
              key={b}
              className="group flex aspect-[3/2] items-center justify-center bg-card p-6 transition-colors hover:bg-surface-elevated"
            >
              <span className="font-display text-lg font-semibold text-muted-foreground transition-colors group-hover:text-foreground">
                {b}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Promotions ---------------- */

function Promotions() {
  const { t } = useI18n();
  const icons = [
    <Sparkles key="s" className="h-5 w-5" />,
    <Handshake key="h" className="h-5 w-5" />,
    <Clock key="c" className="h-5 w-5" />,
  ];
  return (
    <section className="py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary">
              {t.promotions.eyebrow}
            </div>
            <h2 className="mt-3 font-display text-4xl font-bold tracking-tight sm:text-5xl">
              {t.promotions.title}
            </h2>
          </div>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {t.promotions.items.map((p, i) => (
            <div
              key={p.title}
              className="group relative overflow-hidden rounded-2xl border border-border bg-card p-8 transition-all hover:-translate-y-1 hover:border-primary/50 hover:shadow-[var(--shadow-elevated)]"
            >
              <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-primary/10 blur-3xl transition-opacity group-hover:opacity-100" />
              <div className="relative">
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-primary/10 text-primary">
                  {icons[i]}
                </div>
                <h3 className="mt-6 font-display text-xl font-semibold">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
                <Link
                  to="/#contact"
                  className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-primary hover:underline"
                >
                  {p.cta} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Financing ---------------- */

function Financing() {
  const { t } = useI18n();
  const icons = [
    <Wallet key="w" className="h-5 w-5" />,
    <ShieldCheck key="s" className="h-5 w-5" />,
    <Award key="a" className="h-5 w-5" />,
  ];
  return (
    <section id="financing" className="relative overflow-hidden border-y border-border bg-surface py-24">
      <div className="mx-auto grid max-w-7xl gap-16 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary">
            {t.financing.eyebrow}
          </div>
          <h2 className="mt-3 font-display text-4xl font-bold tracking-tight sm:text-5xl">
            {t.financing.title}
          </h2>
          <p className="mt-5 max-w-lg text-muted-foreground">{t.financing.copy}</p>
          <div className="mt-8 space-y-5">
            {t.financing.perks.map((p, i) => (
              <div key={p.title} className="flex items-start gap-4">
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                  {icons[i]}
                </div>
                <div>
                  <div className="font-display font-semibold">{p.title}</div>
                  <div className="mt-1 text-sm text-muted-foreground">{p.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative rounded-3xl border border-border bg-card p-8 shadow-[var(--shadow-elevated)]">
          <div className="absolute -inset-px -z-10 rounded-3xl bg-gradient-to-br from-primary/40 via-transparent to-transparent opacity-40 blur-md" />
          <h3 className="font-display text-xl font-semibold">{t.financing.estimatorTitle}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t.financing.estimatorHint}</p>
          <PaymentCalc />
        </div>
      </div>
    </section>
  );
}

function PaymentCalc() {
  const { t, tf } = useI18n();
  const [price, setPrice] = useState(120000);
  const [down, setDown] = useState(20);
  const [term, setTerm] = useState(60);
  const [apr, setApr] = useState(5.9);

  const principal = price * (1 - down / 100);
  const r = apr / 100 / 12;
  const monthly = r === 0 ? principal / term : (principal * r) / (1 - Math.pow(1 + r, -term));

  return (
    <div className="mt-6 space-y-5">
      <RangeRow label={t.financing.vehiclePrice} value={`$${price.toLocaleString()}`}>
        <input
          type="range"
          min={40000}
          max={300000}
          step={1000}
          value={price}
          onChange={(e) => setPrice(+e.target.value)}
          className="calc-range"
        />
      </RangeRow>
      <RangeRow label={t.financing.downPayment} value={`${down}%`}>
        <input
          type="range"
          min={0}
          max={60}
          value={down}
          onChange={(e) => setDown(+e.target.value)}
          className="calc-range"
        />
      </RangeRow>
      <RangeRow label={t.financing.term} value={tf(t.financing.termUnit, { n: term })}>
        <input
          type="range"
          min={24}
          max={84}
          step={12}
          value={term}
          onChange={(e) => setTerm(+e.target.value)}
          className="calc-range"
        />
      </RangeRow>
      <RangeRow label={t.financing.apr} value={`${apr.toFixed(1)}%`}>
        <input
          type="range"
          min={3.9}
          max={9.9}
          step={0.1}
          value={apr}
          onChange={(e) => setApr(+e.target.value)}
          className="calc-range"
        />
      </RangeRow>

      <div className="mt-6 flex items-end justify-between rounded-xl bg-surface p-5">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            {t.financing.estimatedMonthly}
          </div>
          <div className="mt-1 font-display text-3xl font-bold text-primary">
            ${Math.round(monthly).toLocaleString()}
          </div>
        </div>
        <Link
          to="/#contact"
          className="inline-flex h-11 items-center rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-glow)]"
        >
          {t.financing.getPreApproved}
        </Link>
      </div>

      <style>{`
        .calc-range { width: 100%; accent-color: var(--color-primary); }
      `}</style>
    </div>
  );
}

function RangeRow({
  label,
  value,
  children,
}: {
  label: string;
  value: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          {label}
        </label>
        <span className="font-display text-sm font-semibold">{value}</span>
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

/* ---------------- Testimonials ---------------- */

function Testimonials() {
  const { t } = useI18n();
  return (
    <section className="py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary">
            {t.testimonials.eyebrow}
          </div>
          <h2 className="mt-3 font-display text-4xl font-bold tracking-tight sm:text-5xl">
            {t.testimonials.title}
          </h2>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {t.testimonials.items.map((item) => (
            <figure
              key={item.name}
              className="group relative rounded-2xl border border-border bg-card p-8 transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-[var(--shadow-elevated)]"
            >
              <div className="flex gap-0.5 text-primary">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <blockquote className="mt-5 text-sm leading-relaxed text-foreground/90">
                "{item.body}"
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3 border-t border-border pt-5">
                <div className="grid h-10 w-10 place-items-center rounded-full bg-gradient-to-br from-primary to-primary/40 font-display text-sm font-bold text-primary-foreground">
                  {item.name.charAt(0)}
                </div>
                <div>
                  <div className="text-sm font-semibold">{item.name}</div>
                  <div className="text-xs text-muted-foreground">{item.role}</div>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- About ---------------- */

function About() {
  const { t } = useI18n();
  return (
    <section id="about" className="border-t border-border bg-surface py-24">
      <div className="mx-auto grid max-w-7xl gap-16 px-4 sm:px-6 lg:grid-cols-[1fr_1.2fr] lg:px-8">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary">
            {t.about.eyebrow}
          </div>
          <h2 className="mt-3 font-display text-4xl font-bold tracking-tight sm:text-5xl">
            {t.about.title}
          </h2>
        </div>
        <div>
          <p className="text-lg leading-relaxed text-foreground/85">{t.about.p1}</p>
          <p className="mt-5 text-muted-foreground">{t.about.p2}</p>
          <div className="mt-10 grid grid-cols-3 gap-6 border-t border-border pt-8">
            {t.about.stats.map(([k, v]) => (
              <div key={k}>
                <div className="font-display text-3xl font-bold text-primary">{k}</div>
                <div className="mt-1 text-xs uppercase tracking-widest text-muted-foreground">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- FAQ ---------------- */

function FAQ() {
  const { t } = useI18n();

  return (
    <section id="faq" className="py-24">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_1.5fr] lg:px-8">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary">
            {t.faq.eyebrow}
          </div>
          <h2 className="mt-3 font-display text-4xl font-bold tracking-tight sm:text-5xl">
            {t.faq.title}
          </h2>
          <p className="mt-4 text-muted-foreground">{t.faq.copy}</p>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {t.faq.items.map((it, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-border">
              <AccordionTrigger className="text-left font-display text-base font-semibold hover:no-underline">
                {it.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                {it.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

/* ---------------- Contact ---------------- */

function Contact({ vehicles }: { vehicles: Vehicle[] }) {
  const { t } = useI18n();
  return (
    <section
      id="contact"
      className="relative overflow-hidden border-t border-border bg-gradient-to-b from-background to-surface py-24"
    >
      <div className="mx-auto grid max-w-7xl gap-16 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary">
            {t.contact.eyebrow}
          </div>
          <h2 className="mt-3 font-display text-4xl font-bold tracking-tight sm:text-5xl">
            {t.contact.title}
          </h2>
          <p className="mt-5 max-w-lg text-muted-foreground">{t.contact.copy}</p>

          <div className="mt-10 space-y-5">
            {[
              {
                icon: <MapPin className="h-5 w-5" />,
                title: t.contact.showroom,
                body: siteLocation.addressFull,
              },
              {
                icon: <Phone className="h-5 w-5" />,
                title: t.contact.direct,
                body: `${siteLocation.phoneDisplay} · ${t.contact.hours}`,
              },
              {
                icon: <Mail className="h-5 w-5" />,
                title: t.contact.concierge,
                body: siteLocation.email,
              },
            ].map((c) => (
              <div key={c.title} className="flex items-start gap-4">
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                  {c.icon}
                </div>
                <div>
                  <div className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                    {c.title}
                  </div>
                  <div className="mt-1 text-foreground">{c.body}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 overflow-hidden rounded-2xl border border-border">
            <iframe
              title={t.contact.mapTitle}
              src={siteLocation.mapEmbedUrl}
              className="h-64 w-full grayscale-[35%]"
              loading="lazy"
            />
          </div>
        </div>

        <ContactForm vehicles={vehicles} />
      </div>
    </section>
  );
}

function ContactForm({ vehicles }: { vehicles: Vehicle[] }) {
  const { t } = useI18n();
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const vehicleId = String(fd.get("vehicleId") || "");
    setState("sending");
    try {
      await repositories.leads.create({
        name: String(fd.get("name") || "").trim(),
        email: String(fd.get("email") || "").trim(),
        phone: String(fd.get("phone") || "").trim(),
        vehicleId: vehicleId || undefined,
        source: "Contact Form",
        status: "New",
        priority: "Medium",
        assignedTo: "Concierge Desk",
        message: String(fd.get("message") || "").trim(),
        tags: ["Website"],
        value: vehicles.find((v) => v.id === vehicleId)?.price ?? 0,
      });
      setState("sent");
      form.reset();
    } catch (error) {
      setState("idle");
      toast.error(error instanceof Error ? error.message : t.contact.sendError);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className="relative rounded-3xl border border-border bg-card p-8 shadow-[var(--shadow-elevated)]"
    >
      <div className="absolute -inset-px -z-10 rounded-3xl bg-gradient-to-br from-primary/30 via-transparent to-transparent opacity-40 blur-md" />
      <h3 className="font-display text-2xl font-semibold">{t.contact.formTitle}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{t.contact.formHint}</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <Field label={t.contact.fullName}>
          <input name="name" required className="input" placeholder={t.contact.namePlaceholder} />
        </Field>
        <Field label={t.contact.email}>
          <input
            name="email"
            required
            type="email"
            className="input"
            placeholder={t.contact.emailPlaceholder}
          />
        </Field>
        <Field label={t.contact.phone}>
          <input name="phone" className="input" placeholder={t.contact.phonePlaceholder} />
        </Field>
        <Field label={t.contact.interestedIn}>
          <select name="vehicleId" className="input" defaultValue="">
            <option value="">{t.contact.anyVehicle}</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.year} {v.brand} {v.model}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t.contact.message} full>
          <textarea
            name="message"
            rows={4}
            className="input"
            placeholder={t.contact.messagePlaceholder}
          />
        </Field>
      </div>

      <button
        disabled={state !== "idle"}
        className="mt-6 inline-flex h-12 w-full items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition disabled:opacity-70"
      >
        {state === "idle" && t.contact.send}
        {state === "sending" && t.contact.sending}
        {state === "sent" && t.contact.sent}
      </button>
      <p className="mt-3 text-center text-xs text-muted-foreground">{t.contact.privacyNote}</p>

      <style>{`
        .input {
          width: 100%;
          padding: 0.75rem 1rem;
          border-radius: 0.75rem;
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          font-size: 0.875rem;
          color: var(--color-foreground);
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .input:focus {
          outline: none;
          border-color: var(--color-primary);
          box-shadow: 0 0 0 3px oklch(0.72 0.17 245 / 0.15);
        }
        .input::placeholder { color: var(--color-muted-foreground); opacity: 0.7; }
      `}</style>
    </form>
  );
}

function Field({
  label,
  children,
  full,
}: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
}) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}
